"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Icons ──────────────────────────────────────────────────────────────

function BuildingIcon({ x, y, size = 14, color }: { x: number; y: number; size?: number; color: string }) {
  const s = size;
  return (
    <g opacity={0.6}>
      <rect x={x} y={y + s * 0.15} width={s * 0.6} height={s * 0.85} rx={1.5} fill={color} opacity={0.4} />
      <rect x={x + s * 0.35} y={y} width={s * 0.65} height={s} rx={1.5} fill={color} opacity={0.3} />
    </g>
  );
}

function PersonIcon({ x, y, size = 16, color }: { x: number; y: number; size?: number; color: string }) {
  const s = size;
  return (
    <g opacity={0.65}>
      <circle cx={x + s / 2} cy={y + s * 0.25} r={s * 0.22} fill={color} />
      <ellipse cx={x + s / 2} cy={y + s * 0.75} rx={s * 0.35} ry={s * 0.25} fill={color} />
    </g>
  );
}

// ── Connector helpers ──────────────────────────────────────────────────

function VerticalLine({ x, y1, y2, color, opacity = 0.15 }: { x: number; y1: number; y2: number; color: string; opacity?: number }) {
  return <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth="1.2" opacity={opacity} />;
}

function HorizontalLine({ y, x1, x2, color, opacity = 0.12 }: { y: number; x1: number; x2: number; color: string; opacity?: number }) {
  return <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth="1.2" opacity={opacity} />;
}

// ── Data ───────────────────────────────────────────────────────────────

const STRINGS: Record<
  Locale,
  {
    investors: string[];
    you: string;
    netProfit: string;
    netProfitAmount: string;
    useOfProfit: string;
    dividendTotal: string;
    dividendTotalAmount: string;
    payoutDetail: string[];
    retained: string;
    retainedAmount: string;
    retainedDetail: string[];
    dps: string;
    dpsAmount: string;
    dpsFormula: string;
    perHolding: string;
    sharesTimes: (shares: number) => string;
    amount: (value: string) => string;
    title: string;
    description: string;
    company: string;
    shareholders: string;
    footnote: string;
  }
> = {
  ja: {
    investors: ["投資家 A", "あなた", "投資家 B"],
    you: "あなた",
    netProfit: "当期純利益",
    netProfitAmount: "300億円",
    useOfProfit: "使い道",
    dividendTotal: "配当総額",
    dividendTotalAmount: "100億円",
    payoutDetail: ["配当性向 33%", "（= 100億 ÷ 300億）"],
    retained: "社内に残す分",
    retainedAmount: "200億円",
    retainedDetail: ["R&D・設備投資", "借入返済・手元資金 など"],
    dps: "1株あたり配当金",
    dpsAmount: "100円",
    dpsFormula: "（= 配当総額 100億円 ÷ 発行済み株式数 1億株）",
    perHolding: "× 持株数で分配",
    sharesTimes: (shares) => `${shares}株 × 100円`,
    amount: (value) => `${value}円`,
    title: "利益はどう使われるのか",
    description: "会社が配当に回した分は、持株数に応じて株主に分配される",
    company: "会社",
    shareholders: "投資家（株主）",
    footnote: "持株数に応じて同じ1株配当が届く",
  },
  en: {
    investors: ["Investor A", "You", "Investor B"],
    you: "You",
    netProfit: "Net profit",
    netProfitAmount: "¥30.0bn",
    useOfProfit: "Where it goes",
    dividendTotal: "Total dividends",
    dividendTotalAmount: "¥10.0bn",
    payoutDetail: ["Payout ratio 33%", "(= ¥10.0bn ÷ ¥30.0bn)"],
    retained: "Retained in the business",
    retainedAmount: "¥20.0bn",
    retainedDetail: ["R&D and capex", "Debt repayment, cash on hand, etc."],
    dps: "Dividend per share",
    dpsAmount: "¥100",
    dpsFormula: "(= ¥10.0bn total dividends ÷ 100m shares outstanding)",
    perHolding: "× split by shares held",
    sharesTimes: (shares) => `${shares} shares × ¥100`,
    amount: (value) => `¥${value}`,
    title: "Where the profit goes",
    description: "What the company allocates to dividends is split among shareholders by how many shares they hold",
    company: "Company",
    shareholders: "Investors (shareholders)",
    footnote: "Every shareholder receives the same dividend per share",
  },
};

const INVESTOR_SHARES = [500, 100, 200];

const PER_SHARE_DIVIDEND = 100;

// ── Layout constants ───────────────────────────────────────────────────

const W = 400;
const PAD = 16;
const CONTENT_W = W - PAD * 2;
const ZONE_PAD = 12;
const ZONE_INNER_W = CONTENT_W - ZONE_PAD * 2;
const INNER_L = PAD + ZONE_PAD;

// ── Sub-components ─────────────────────────────────────────────────────

/** 背景つきゾーン枠 */
function ZoneBox({ y, height, color }: { y: number; height: number; color: string }) {
  return (
    <rect
      x={PAD} y={y} width={CONTENT_W} height={height} rx={10}
      fill={color} opacity={0.04}
      stroke={color} strokeWidth="1" strokeOpacity={0.1}
    />
  );
}

/** ゾーンラベル（アイコン + テキスト） */
function ZoneLabel({
  y,
  icon,
  text,
  color,
}: {
  y: number;
  icon: "building" | "person";
  text: string;
  color: string;
}) {
  const iconY = y + ZONE_PAD + 1;
  const iconX = PAD + ZONE_PAD;
  return (
    <g>
      {icon === "building" ? (
        <BuildingIcon x={iconX} y={iconY} size={14} color={color} />
      ) : (
        <PersonIcon x={iconX} y={iconY} size={14} color={color} />
      )}
      <text x={iconX + 18} y={y + ZONE_PAD + 12} fontSize="10" fill={color} fontWeight="700" opacity={0.55}>
        {text}
      </text>
    </g>
  );
}

/** 当期純利益バー */
function ProfitBar({ y, height, color }: { y: number; height: number; color: string }) {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      <rect x={INNER_L} y={y} width={ZONE_INNER_W} height={height} rx={6} fill={color} opacity={0.07} />
      <text x={INNER_L + 12} y={y + height / 2} fontSize="11" fill={color} fontWeight="600" dominantBaseline="central" opacity={0.7}>
        {t.netProfit}
      </text>
      <text x={INNER_L + ZONE_INNER_W - 12} y={y + height / 2} textAnchor="end" fontSize="12" fill={color} fontWeight="700" dominantBaseline="central" opacity={0.8}>
        {t.netProfitAmount}
      </text>
    </g>
  );
}

/** 使い道コネクター（当期純利益 → 配当総額 / 社内に残す分） */
function SplitConnector({
  topY,
  height,
  bottomY,
  leftCx,
  rightCx,
  color,
}: {
  topY: number;
  height: number;
  bottomY: number;
  leftCx: number;
  rightCx: number;
  color: string;
}) {
  const t = STRINGS[useDocLocale()];
  const cx = PAD + CONTENT_W / 2;
  const midY = topY + height / 2;
  return (
    <g>
      <VerticalLine x={cx} y1={topY} y2={midY - 8} color={color} />
      <text x={cx} y={midY - 2} textAnchor="middle" fontSize="9" fill={color} opacity={0.5} fontWeight="600">
        {t.useOfProfit}
      </text>
      <HorizontalLine y={midY + 6} x1={leftCx} x2={rightCx} color={color} />
      <VerticalLine x={leftCx} y1={midY + 6} y2={bottomY} color={color} opacity={0.12} />
      <VerticalLine x={rightCx} y1={midY + 6} y2={bottomY} color={color} opacity={0.12} />
    </g>
  );
}

/** 分割ボックス（配当総額 or 社内に残す分） */
function SplitBox({
  x,
  y,
  width,
  height,
  color,
  highlighted,
  title,
  amount,
  details,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  highlighted: boolean;
  title: string;
  amount: string;
  details: string[];
}) {
  return (
    <g>
      <rect
        x={x} y={y} width={width} height={height} rx={6}
        fill={color} opacity={highlighted ? 0.08 : 0.05}
        stroke={color} strokeWidth="1" strokeOpacity={highlighted ? 0.15 : 0.1}
      />
      <text x={x + 10} y={y + 15} fontSize="11" fill={color} fontWeight="700" opacity={highlighted ? 0.7 : 0.45}>
        {title}
      </text>
      <text x={x + width - 10} y={y + 15} textAnchor="end" fontSize="12" fill={color} fontWeight="700" opacity={highlighted ? 0.8 : 0.45}>
        {amount}
      </text>
      {details.map((line, i) => (
        <text key={i} x={x + 10} y={y + 30 + i * 11} fontSize="8.5" fill={color} opacity={0.7}>
          {line}
        </text>
      ))}
    </g>
  );
}

/** 1株あたり配当金の結果バー */
function PerShareResult({ y, height, color }: { y: number; height: number; color: string }) {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      <rect
        x={INNER_L} y={y} width={ZONE_INNER_W} height={height} rx={6}
        fill={color} opacity={0.12}
        stroke={color} strokeWidth="1.5" strokeOpacity={0.35}
      />
      <text x={INNER_L + 12} y={y + 15} fontSize="11" fill={color} fontWeight="700">
        {t.dps}
      </text>
      <text x={INNER_L + ZONE_INNER_W - 12} y={y + 15} textAnchor="end" fontSize="13" fill={color} fontWeight="800">
        {t.dpsAmount}
      </text>
      <text x={INNER_L + 12} y={y + 31} fontSize="8" fill={color} opacity={0.7}>
        {t.dpsFormula}
      </text>
    </g>
  );
}

/** 会社→投資家の分配コネクター */
function DistributionConnector({
  topY,
  bottomY,
  investorCxList,
  color,
}: {
  topY: number;
  bottomY: number;
  investorCxList: number[];
  color: string;
}) {
  const t = STRINGS[useDocLocale()];
  const cx = PAD + CONTENT_W / 2;
  const midY = topY + (bottomY - topY) / 2;
  return (
    <g>
      <VerticalLine x={cx} y1={topY + 2} y2={midY - 6} color={color} />
      <text x={cx + 8} y={midY - 1} fontSize="9" fill={color} fontWeight="600" opacity={0.55}>
        {t.perHolding}
      </text>
      {investorCxList.map((cardCx, i) => (
        <g key={i}>
          <line x1={cx} y1={midY + 4} x2={cardCx} y2={bottomY} stroke={color} strokeWidth="1" opacity={0.15} />
          <circle cx={cardCx} cy={bottomY} r={2} fill={color} opacity={0.3} />
        </g>
      ))}
    </g>
  );
}

/** 投資家カード */
function InvestorCard({
  x,
  y,
  width,
  height,
  label,
  shares,
  accentColor,
  grayColor,
  isHighlighted,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  shares: number;
  accentColor: string;
  grayColor: string;
  isHighlighted: boolean;
}) {
  const t = STRINGS[useDocLocale()];
  const color = isHighlighted ? accentColor : grayColor;
  const amount = (shares * PER_SHARE_DIVIDEND).toLocaleString();
  return (
    <g>
      <rect
        x={x} y={y} width={width} height={height} rx={8}
        fill={color} opacity={isHighlighted ? 0.14 : 0.06}
        stroke={color} strokeWidth={isHighlighted ? "1.5" : "1"} strokeOpacity={isHighlighted ? 0.45 : 0.15}
      />
      <PersonIcon x={x + 8} y={y + 5} size={13} color={color} />
      <text x={x + 24} y={y + 14} fontSize="10" fill={color} fontWeight="700" opacity={isHighlighted ? 1.0 : 0.6}>
        {label}
      </text>
      <text x={x + 10} y={y + 28} fontSize="8.5" fill={grayColor} opacity={0.55}>
        {t.sharesTimes(shares)}
      </text>
      <text x={x + width - 10} y={y + 42} textAnchor="end" fontSize="13" fill={color} fontWeight="800" opacity={isHighlighted ? 0.95 : 0.55}>
        {t.amount(amount)}
      </text>
    </g>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export function DocDividendAllocationDiagram() {
  const t = STRINGS[useDocLocale()];
  const BLUE = COLOR.sky;
  const GRAY = "currentColor";

  // ── 会社ゾーン ──
  const compZoneY = 4;
  const labelH = 22;
  const profitBarY = compZoneY + ZONE_PAD + labelH;
  const profitBarH = 30;

  // 使い道コネクター
  const splitConnTop = profitBarY + profitBarH;
  const splitConnH = 28;

  // 分割ボックス（配当総額 / 社内に残す分）
  const splitGap = 12;
  const splitBoxW = (ZONE_INNER_W - splitGap) / 2;
  const splitY = splitConnTop + splitConnH;
  const splitBoxH = 46;
  const leftBoxX = INNER_L;
  const rightBoxX = INNER_L + splitBoxW + splitGap;

  // 1株あたり配当金
  const resultY = splitY + splitBoxH + 10;
  const resultH = 40;

  const compZoneH = ZONE_PAD + labelH + profitBarH + splitConnH + splitBoxH + 10 + resultH + ZONE_PAD + 4;

  // ── ゾーン間コネクター ──
  const gapH = 32;

  // ── 投資家ゾーン ──
  const invZoneY = compZoneY + compZoneH + gapH + 4;
  const invContentY = invZoneY + ZONE_PAD + labelH;
  const invCardH = 50;
  const invGap = 10;
  const invCardW = (ZONE_INNER_W - invGap * 2) / 3;
  const invZoneH = ZONE_PAD + labelH + invCardH + ZONE_PAD + 8;

  const totalH = invZoneY + invZoneH + 10;

  // 投資家カードの中央X座標リスト
  const investorCxList = INVESTOR_SHARES.map((_, i) => INNER_L + i * (invCardW + invGap) + invCardW / 2);

  return (
    <IllustrationCard title={t.title} description={t.description}>
      <svg viewBox={`0 0 ${W} ${totalH}`} className="mx-auto w-full max-w-xl">
        {/* ── 会社ゾーン ── */}
        <ZoneBox y={compZoneY} height={compZoneH} color={GRAY} />
        <ZoneLabel y={compZoneY} icon="building" text={t.company} color={GRAY} />

        <ProfitBar y={profitBarY} height={profitBarH} color={GRAY} />

        <SplitConnector
          topY={splitConnTop}
          height={splitConnH}
          bottomY={splitY}
          leftCx={leftBoxX + splitBoxW / 2}
          rightCx={rightBoxX + splitBoxW / 2}
          color={GRAY}
        />

        <SplitBox
          x={leftBoxX} y={splitY} width={splitBoxW} height={splitBoxH}
          color={COLOR.emerald} highlighted
          title={t.dividendTotal} amount={t.dividendTotalAmount}
          details={t.payoutDetail}
        />

        <SplitBox
          x={rightBoxX} y={splitY} width={splitBoxW} height={splitBoxH}
          color={GRAY} highlighted={false}
          title={t.retained} amount={t.retainedAmount}
          details={t.retainedDetail}
        />

        {/* 配当総額 → 1株あたり配当金 */}
        <VerticalLine x={leftBoxX + splitBoxW / 2} y1={splitY + splitBoxH} y2={resultY} color={GRAY} opacity={0.12} />
        <PerShareResult y={resultY} height={resultH} color={BLUE} />

        {/* ── 会社 → 投資家 コネクター ── */}
        <DistributionConnector
          topY={compZoneY + compZoneH}
          bottomY={invContentY}
          investorCxList={investorCxList}
          color={GRAY}
        />

        {/* ── 投資家ゾーン ── */}
        <ZoneBox y={invZoneY} height={invZoneH} color={GRAY} />
        <ZoneLabel y={invZoneY} icon="person" text={t.shareholders} color={GRAY} />

        {INVESTOR_SHARES.map((shares, i) => (
          <InvestorCard
            key={t.investors[i]}
            x={INNER_L + i * (invCardW + invGap)}
            y={invContentY}
            width={invCardW}
            height={invCardH}
            label={t.investors[i]!}
            shares={shares}
            accentColor={BLUE}
            grayColor={GRAY}
            isHighlighted={t.investors[i] === t.you}
          />
        ))}

        <text x={W / 2} y={invZoneY + invZoneH - 4} textAnchor="middle" fontSize="8" fill={GRAY} opacity={0.4}>
          {t.footnote}
        </text>
      </svg>
    </IllustrationCard>
  );
}
